import { AfterViewInit, Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Options } from 'tabulator-tables';
import { isEmpty, forEach, isArray, map } from 'lodash';
import {
  ActiveTestQueuedStateService,
  Device,
  DevicesService,
  FusionTransformationService,
  IDeviceData,
  TabulatorService,
  TestMode,
  UnmanagedDevicesService,
  WarningMessage
} from '@nrwl/common-lib';
import { ViaviTableComponent } from '@nrwl/viavi-tabulator';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AgentSpecComponent } from '@nrwl/datasource-management';

export enum DataSource {
  GIGA_TEST = 'GigaTestAgent',
  UNMANAGED = 'unmanaged'
}
@Component({
  selector: 'nrwl-agent-list',
  templateUrl: './agent-list.component.html',
  styleUrls: ['./agent-list.component.scss']
})
export class AgentListComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(ViaviTableComponent) deviceTable: ViaviTableComponent;
  table: Options;
  //eslint-disable-next-line
  deviceListData: any;
  deviceWithMonitors = [];
  selectedDevices = [];
  opened: boolean;
  unmanagedDevicesList = [];
  deviceDataSource: Array<Device>;
  private readonly unsubscribe: Subject<void> = new Subject();
  private cacheObj: Subscription;
  private unmanagedCacheObj: Subscription;
  constructor(
    private deviceService: DevicesService,
    public dialogRef: MatDialogRef<AgentListComponent>,
    private tabulatorService: TabulatorService,
    private fusionTransformationService: FusionTransformationService,
    private unmanagedDevicesService: UnmanagedDevicesService,
    private readonly queuedService: ActiveTestQueuedStateService,
    private dialog: MatDialog,
    //eslint-disable-next-line
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.dialog = dialog;
    this.table = this.tabulatorService.commonTableConfiguration();
    this.table.persistenceID = 'device-selection';
    this.table.groupHeader = this.tabulatorService.groupHeader;
  }

  ngOnInit(): void {
    const deviceCache = this.data.selectReflectorDevice
      ? this.deviceService.reflectorsDeviceCache
      : this.deviceService.cache;
    this.queuedService.usedTestCountCache.pipe(takeUntil(this.unsubscribe)).subscribe(monitors => {
      if (monitors?.length) {
        this.deviceWithMonitors = monitors;
        this.getFormattedData();
      }
    });
    this.cacheObj = deviceCache.subscribe(res => {
      this.deviceDataSource = res;
      this.getFormattedData();
    });
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.opened = true;
    }, 300);
  }

  getFormattedData(): void {
    let formattedData = this.transformResponse(this.deviceDataSource);
    formattedData = formattedData.filter(
      device =>
        device.status === 'online' &&
        this.data.agentTypes.some(
          item => device.dataItem?.model?.model.toLowerCase() === item.toLowerCase()
        )
    );
    if (this.data.filteredData?.length) {
      formattedData = formattedData.filter(item => item.id !== this.data.filteredData[0].id);
      if (this.data?.destinationFilter?.length) {
        // Destination agent filter based on source Agent selected
        const selectedSourceAgentModel = this.data.filteredData[0]?.dataItem?.model?.model;
        this.data.destinationFilter.forEach(item => {
          if (item?.sourceAgent.includes(selectedSourceAgentModel.toLowerCase())) {
            formattedData = formattedData.filter(
              agents =>
                agents.dataItem?.model?.model &&
                item?.destinationAgent.includes(agents.dataItem?.model?.model.toLowerCase())
            );
          }
        });
      }
    }
    if (this.data.agentCapabilityCheck) {
      formattedData = formattedData.filter(device => {
        return device?.dataItem?.capabilities?.phy
          ? device?.dataItem?.capabilities?.[this.data.agentCapabilityCheck.section] &&
              device?.dataItem?.capabilities?.[this.data.agentCapabilityCheck.section]?.[
                this.data.agentCapabilityCheck.field
              ] === this.data.agentCapabilityCheck.value
          : device?.dataItem?.capabilities?.[this.data.agentCapabilityCheck.section1]?.find(
              item =>
                item[this.data.agentCapabilityCheck.field] ===
                  this.data.agentCapabilityCheck.value && item['phy-id'] === device.phyId
            );
      });
    }

    if (this.data.agentTypes.includes('unmanaged')) {
      this.getUnmanagedDevices();
      formattedData = [...formattedData, ...this.unmanagedDevicesList];
    }
    if (this.data.selectedDevices.length > 0) {
      const result = formattedData.map(el => {
        if (this.data.selectedDevices.find(filteredId => el.id === filteredId.id)) {
          el.isChecked = true;
        }
        return el;
      });
      formattedData = result;
    }
    this.assignData(formattedData);
  }
  //eslint-disable-next-line
  assignData(data: any): void {
    this.deviceListData = this.fusionTransformationService.transformData('device-selection', data);
    this.table.height = 500;
    delete this.table.rowHeight;
    if (this.data.singleSelection) {
      delete this.table.selectable;
      delete this.table.selectableRangeMode;
      this.table.hideSelectAll = true;
    }
    this.table.columns = this.deviceListData.column;
    this.table.data = this.deviceListData.data;
    if (this.table.persistence) {
      this.table.persistence.sort = true;
      this.table.persistence.page = true;
    }
    this.table = Object.assign({}, this.table);
  }
  closeModal(): void {
    const selectedDevices =
      this.deviceTable && this.deviceTable.selection ? this.deviceTable.selection : [];
    this.dialogRef.close(selectedDevices);
    this.deviceService.clearTabulatorCache();
  }

  getUnmanagedDevices(): void {
    this.unmanagedCacheObj = this.unmanagedDevicesService.cache.subscribe(res => {
      const filteredList = res.filter(device => {
        return device?.deviceData?.capabilities?.[this.data.loopbackType];
      });
      this.unmanagedDevicesList = this.transformUnmanagedDevices(filteredList);
    });
  }

  getIconLabelName(labelName: string): { label: string } {
    return {
      label: labelName
    };
  }
  sendSelectedItem(): void {
    if (this.data.singleSelection) {
      const oldRowSelection = Object.assign([], this.deviceTable.table.getSelectedData());
      if (oldRowSelection.length > 1) {
        const x = oldRowSelection[0];
        this.deviceTable.table.deselectRow(x.id);
      }
    }
  }

  openAgentDetailsDialog(data: any): void {
      if (data.dataItem.model?.model.includes('JMEP')) {
        data.dataItem.capabilities.phy = {
          ...data.dataItem.capabilities.phy,
          'test-vlan-id':
            Number(data.dataItem.capabilities.phy['test-vlan-id']) > 0
              ? data.dataItem.capabilities.phy['test-vlan-id']
              : 0,
          'esam-name': data.name.sublabel?.split('ESAM:')[1],
          'esam-ip': data.dataItem.capabilities.phy['management-ip']
        };
        delete data.dataItem.capabilities.phy['management-ip'];
      }
      const dialogData = {
        deviceName: data.dataItem.nameId,
        data: data.dataItem,
        model: data.dataItem?.model?.model
      };
      this.dialog.open(AgentSpecComponent, {
        width: '66rem',
        disableClose: true,
        data: dialogData
      });
    }

  onActionClick(action: any): any {
      this.openAgentDetailsDialog(action.rowData);
  }

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  transformResponse(dataIn: Array<Device>): any[] {
    const tranformedData = [];
    const deviceData = map(dataIn, data => data.deviceData);
    if (!isEmpty(deviceData) && isArray(deviceData)) {
      forEach(deviceData, (item: IDeviceData) => {
        if (
          item.name &&
          item.name.indexOf('_DESTROYED_') < 0 &&
          item.type === DataSource.GIGA_TEST
        ) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const result: any = {};
          switch (item.type) {
            case DataSource.GIGA_TEST:
              result.name = this.getIconLabelName(item.nameId);
              result.id = item.id;
              result.dataType = item.type;
              result.status = item.status;
              result.location = item.location ? item.location : '--';
              result.state = item.state;
              result.dataItem = item;
              result.ipAddress = item.ipaddress;
              result.mgmtIPaddress = item.mgmtIPaddress;
              result.phyId = item.phyId;
              result.testAssigned = this.getTestAssignedDatasource(
                this.getActiveTestQueuedStatus(this.deviceWithMonitors, item.id),
                item.nameId
              );
              result.warningMessage = this.getWarningMessage(
                null,
                item.id,
                item.status,
                this.data.testType
              );
              if (
                item.model.model.includes('JMEP') &&
                this.data.selectedTestMode === TestMode.LOOPBACK &&
                this.data.selectionMode === 'destination'
              ) {
                result.testAssigned = [];
                result.warningMessage = null;
              }
              if (this.data.selectReflectorDevice) {
                result.warningMessage = null;
              }
              break;
          }
          tranformedData.push(result);
        }
      });
    }
    return tranformedData;
  }

  getWarningMessage(siteId: string, agentId: string, status: string, testType?: string): string {
    const message = this.queuedService.warningMessage(agentId, testType);
    if (status === 'offline') {
      return 'Test will be queued/failed. Resources are offline';
    } else if (
      (message === WarningMessage.RESOURCE_AVAILABLE_WARNING_MESSAGE && status === 'online') ||
      !status
    ) {
      return null;
    } else {
      return message;
    }
  }

  //eslint-disable-next-line
  getActiveTestQueuedStatus(deviceWithMonitors: any, id: string): any {
    return deviceWithMonitors.find(device => device.deviceData.id === id);
  }

  // eslint-disable-next-line
  getTestAssignedDatasource(data: any, deviceName: string): any {
    const dataSource = [];
    const testAssignedData = data?.queueStatus;
    for (const key in testAssignedData) {
      if (testAssignedData[key]?.total) {
        dataSource.push({
          type: key,
          used: testAssignedData[key]?.used,
          max: testAssignedData[key]?.total,
          className: this.tabulatorService.getTestTypeImage(key),
          maxCount: testAssignedData['maxCount'],
          overalUsed: testAssignedData['overall_total_used'],
          overalPercentage: Number.isNaN(testAssignedData['overall_percentage'])
            ? -1
            : testAssignedData['overall_percentage'],
          deviceName: deviceName,
          hasOveralPercentage: Number.isInteger(testAssignedData['overall_percentage'])
            ? 'block'
            : 'none',
          showProgressBar: Number.isInteger(testAssignedData['overall_percentage'])
            ? 'none'
            : 'block',
          hideView: true
        });
      }
    }
    return dataSource;
  }

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  transformUnmanagedDevices(dataIn: Array<Device>): any[] {
    const tranformedData = [];
    const deviceData = map(dataIn, data => data.deviceData);
    if (!isEmpty(deviceData) && isArray(deviceData)) {
      forEach(deviceData, (item: IDeviceData) => {
        if (item.name && item.type === DataSource.UNMANAGED) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const result: any = {};
          result.name = this.getIconLabelName(item.name);
          result.id = item.id;
          result.dataType = item.type;
          result.status = item.status;
          result.location = item.location ? item.location : '--';
          result.state = item.state;
          result.dataItem = item;
          result.ipAddress = this.getLoopbackIpAddress(item);
          result.phyId = item.phyId;
          tranformedData.push(result);
        }
      });
    }
    return tranformedData;
  }

  getLoopbackIpAddress(deviceData: IDeviceData): string {
    let loopbackConfig: any;
    if (this.data.loopbackType) {
      loopbackConfig = deviceData?.capabilities?.[this.data.loopbackType];
    }
    return this.data.loopbackType === 'l2-loopback' ? loopbackConfig?.mac : loopbackConfig?.ip;
  }
  ngOnDestroy() {
    if (this.cacheObj) {
      this.cacheObj.unsubscribe();
    }
    if (this.unmanagedCacheObj) {
      this.unmanagedCacheObj.unsubscribe();
    }
  }

  disableSave(): boolean {
    return !this.deviceTable?.selection || this.deviceTable?.selection?.length === 0;
  }
}
